import React, { useState } from 'react';
import { Card, Button, Table, Empty } from 'antd';
import { PieChartOutlined, TableOutlined } from '@ant-design/icons';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import './ProductMixChart.scss';

const ProductMixChart = ({ 
  title, 
  productData = [], 
  loading = false 
}) => {
  const [isTableView, setIsTableView] = useState(false);

  // Color scheme for different products
  const COLORS = {
    credit_1: '#ff4d4f',        // Original vibrant red
    credit_2_6: '#ff6f91',      // Coral pink
    credit_7_12: '#ff9f40',     // Bright orange
    credit_13_21: '#feca57',    // Warm yellow
    credit_22_plus: '#48dbfb',  // Sky blue (distinct endpoint)
    credit: '#1890ff',          // Original bright blue
    debit: '#52c41a',           // Original green
    pix: '#faad14',             // Original orange/amber
    boleto: '#13c2c2',           // Teal/cyan
    default: '#722ed1'          // Original purple
  };

  // Get product labels in Portuguese
  function getProductLabel(product) {
    const labels = {
      credit_1: 'Crédito à Vista',
      credit_2_6: 'Crédito 2 a 6x',
      credit_7_12: 'Crédito 7 a 12x',
      credit_13_21: 'Crédito 13 a 21x',
      credit_22_plus: 'Crédito 22x ou mais',
      credit: 'Crédito',
      debit: 'Débito',
      pix: 'PIX',
      boleto: 'Boleto',
    };
    return labels[product] || product.charAt(0).toUpperCase() + product.slice(1);
  }

  const PRODUCT_ORDER = ['pix', 'boleto', 'debit', 'credit_1', 'credit_2_6', 'credit_7_12', 'credit_13_21', 'credit_22_plus', 'credit'];

  const aggregateProductData = () => {
    const productTotals = {};

    productData.forEach(item => {
      if (productTotals[item.product]) {
        productTotals[item.product] += parseFloat(item.total || 0);
      } else {
        productTotals[item.product] = parseFloat(item.total || 0);
      }
    });

    return Object.entries(productTotals)
      .sort(([a], [b]) => {
        const indexA = PRODUCT_ORDER.indexOf(a);
        const indexB = PRODUCT_ORDER.indexOf(b);
        return (indexA === -1 ? 999 : indexA) - (indexB === -1 ? 999 : indexB);
      })
      .map(([product, total]) => ({
        name: getProductLabel(product),
        value: total / 100,
        color: COLORS[product] || COLORS.default
      }));
  };
  const chartData = aggregateProductData();

  // Table columns configuration
  const tableColumns = [
    {
      title: 'Tipo de Pagamento',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <span style={{ display: 'flex', alignItems: 'center' }}>
          <div 
            style={{ 
              width: '12px', 
              height: '12px', 
              backgroundColor: record.color, 
              borderRadius: '50%', 
              marginRight: '8px' 
            }}
          />
          {text}
        </span>
      ),
    },
    {
      title: 'Valor',
      dataIndex: 'value',
      key: 'value',
      align: 'right',
      render: (value) => `R$ ${value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
    },
  ];

  // Calculate total for table
  const total = chartData.reduce((sum, item) => sum + item.value, 0);

  // Table data with total row
  const tableDataWithTotal = [
    ...chartData.map((item, index) => ({ ...item, key: index })),
    {
      key: 'total',
      name: 'Total',
      value: total,
      color: '#000',
    }
  ];

  const formatTooltipValue = (value, name) => {
    return [
      `R$ ${value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      name
    ];
  };

  const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
    if (percent < 0.05) return null; // Don't show label if less than 5%
    
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text 
        x={x} 
        y={y} 
        fill="white" 
        textAnchor={x > cx ? 'start' : 'end'} 
        dominantBaseline="central"
        fontSize="12"
        fontWeight="bold"
      >
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };  return (
    <Card 
      className="product-mix-chart" 
      title={title}
      loading={loading}
      extra={
        chartData.length > 0 && (
          <Button 
            size="small"
            icon={isTableView ? <PieChartOutlined /> : <TableOutlined />}
            onClick={() => setIsTableView(!isTableView)}
          >
            {isTableView ? 'Gráfico' : 'Detalhes'}
          </Button>
        )
      }
    >
      {chartData.length === 0 ? (
        <div className="empty-state">
          <Empty 
            description="Não há dados disponíveis"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        </div>
      ) : isTableView ? (
        <Table 
          columns={tableColumns}
          dataSource={tableDataWithTotal}
          pagination={false}
          size="small"
          rowClassName={(record) => record.key === 'total' ? 'total-row' : ''}
        />
      ) : (
        <ResponsiveContainer width="100%" height={300}>
          <PieChart>
            <Pie
              data={chartData}
              cx="50%"
              cy="50%"
              labelLine={false}
              label={renderCustomizedLabel}
              outerRadius={100}
              fill="#8884d8"
              dataKey="value"
            >
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Pie>
            <Tooltip 
              formatter={formatTooltipValue}
              contentStyle={{
                background: 'rgba(255, 255, 255, 0.95)',
                border: '1px solid #d9d9d9',
                borderRadius: '6px',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
              }}
            />
            <Legend 
              verticalAlign="bottom" 
              height={36}
              formatter={(value, entry) => (
                <span style={{ color: entry.color, fontWeight: 500 }}>
                  {value}
                </span>
              )}
            />
          </PieChart>
        </ResponsiveContainer>
      )}
    </Card>
  );
};

export default ProductMixChart;
